import { useMemo } from 'react';

import { useAssetMetersQuery } from '../queries/assets';
import { useAppSelector } from '../store/hooks';
import { MeterMeasurement } from '../types/asset';
import { NumberQuestionBase } from '../types/questions';
import { resolveMeterLink } from '../utils/meterLink';

/**
 * The four states the caller has to tell apart. They used to collapse into a bare
 * null, which left the runtime unable to distinguish "still fetching" from "there
 * is nothing to compare against" - and validating against a reference that has not
 * arrived yet is the difference between blocking a bad reading and letting it through.
 */
export type AssetLastMeasurementState = {
  /**
   * Identifies WHICH reference is being resolved, so a caller can tell one request from the
   * next. The status string cannot do that: switching assets mid-flight leaves it on
   * `'loading'`, unchanged, and any timer keyed on it silently keeps running against the
   * previous asset's budget.
   */
  requestKey: string | null;
} & (
  /** No meter link, or no asset picked yet. Nothing to fetch and nothing to validate. */
  | { status: 'not-applicable'; measurement: null }
  /** A request is in flight. The caller should hold submission until it settles. */
  | { status: 'loading'; measurement: null }
  /** The request failed. The caller should not block: field capture comes first. */
  | { status: 'unavailable'; measurement: null }
  /** Settled. The measurement is null when the asset has no reading for this meter. */
  | { status: 'ready'; measurement: MeterMeasurement | null }
);

/**
 * Returns the last measurement for the current number question when it is linked
 * to an "assets" select_metadata question via meterConfigurations and an asset is selected.
 * Only applies when the associated question is an assets list and has meter configs.
 *
 * The assets check accepts either key on purpose. The same question carries two
 * shapes depending on the surface: the builder authors `metadata`, and the backend
 * derives `metadata_code` from it when serving the form to be answered
 * (datascope-rails task_form.rb:102). Matching only one of them makes the predicate
 * pass on one surface and fail on the other, which is how DS-3945 found its
 * comparator permanently disabled in the builder.
 *
 * Note that `metadata` is not the same type on both sides: the builder holds the
 * code as a string, and the served form replaces it with the list's rows
 * (task_form.rb:100-101). So only the literal string counts as the marker, and the
 * field is typed as unknown rather than string to keep that honest.
 */
export function useAssetLastMeasurement(question: NumberQuestionBase): AssetLastMeasurementState {
  const questions = useAppSelector((state) => state.webForms.questions);
  const answers = useAppSelector((state) => state.webForms.answers);
  const metadataMap = useAppSelector((state) => state.webForms.metadata);

  const { meterId, selectedAssetId } = useMemo(() => {
    // The link predicate lives in utils/meterLink and is shared with the form builder. It used to
    // be reimplemented here, and the two copies had already drifted: only one of them looked
    // through table nesting, so a meter question inside a table resolved on one surface and not
    // the other. One definition is the whole point of the predicate.
    const link = resolveMeterLink(question, questions as never);
    if (!link) return { meterId: null, selectedAssetId: null };

    // The asset question comes back from the resolution rather than being looked up again. The
    // lookup that used to live here did not flatten table nesting, so for an asset question
    // inside a table the predicate reported a live link and this lookup found nothing: the hook
    // produced no banner and said nothing, with its two halves disagreeing about the same form.
    const assetAnswer = answers[link.assetQuestion.id]?.value;
    if (typeof assetAnswer !== 'string' || !assetAnswer)
      return { meterId: link.meterId, selectedAssetId: null };

    const assets = metadataMap['assets'] as { id: number; name: string }[] | undefined;
    const asset = assets?.find((entry) => entry.name === assetAnswer);

    return { meterId: link.meterId, selectedAssetId: asset?.id ?? null };
  }, [question, questions, answers, metadataMap]);

  const assetIdForMeters = selectedAssetId != null && meterId != null ? selectedAssetId : null;
  // isFetching, not isPending and not isLoading, and only while there is nothing cached yet.
  //
  // isPending is out because a disabled query stays pending forever under React Query v5, which
  // would hold submission on every question with no asset selected; isFetching is false while
  // disabled, so it keeps that property.
  //
  // isLoading was the previous choice and is `isPending && isFetching`, so it goes false during a
  // background revalidation: the hook then reported 'ready' carrying the OLD measurement while
  // the server was already answering with a newer one.
  //
  // But reporting 'loading' on EVERY fetch was worse, and reachable in normal use. This query
  // sets `staleTime: 0` and the client leaves `refetchOnWindowFocus` at its default, so coming
  // back to the tab triggers a refetch. With a bare `isFetching` check that refetch dropped the
  // measurement to null, which made the validation effect re-run against no reference, clear the
  // blocking error, and then release the hold at the 5s ceiling: a reading that was blocked
  // became submittable just because the person changed windows. For a mechanism whose failure
  // mode is the silent pass, that trade is the wrong way round.
  //
  // So: hold only when there is nothing to validate against, and keep serving the cached reading
  // while a background revalidation is in flight.
  const { data: meters, isFetching, isError } = useAssetMetersQuery(assetIdForMeters, false);

  const requestKey = assetIdForMeters == null ? null : `${assetIdForMeters}:${meterId}`;

  return useMemo<AssetLastMeasurementState>(() => {
    if (assetIdForMeters == null || meterId == null) {
      return { status: 'not-applicable', measurement: null, requestKey };
    }
    // Both non-ready states live under the same guard, and the rule they share is the one the
    // comment above states: hold, or give up, only when there is nothing to validate against.
    //
    // The error branch used to sit outside it, which put the silent pass one level down instead
    // of removing it. React Query keeps `data` when a refetch fails, so a focus refetch that
    // failed while a reading was cached reported 'unavailable', the validation effect re-ran
    // with a null reference, and the blocking error was cleared - against a reference that was
    // still in hand. `retry: 0` in App.tsx means one failure is enough.
    if (!meters) {
      if (isFetching) return { status: 'loading', measurement: null, requestKey };
      return { status: 'unavailable', measurement: null, requestKey };
    }
    if (!Array.isArray(meters)) {
      return { status: 'unavailable', measurement: null, requestKey };
    }

    const meter = meters.find((meterEntry) => String(meterEntry.meter_id) === String(meterId));
    return { status: 'ready', measurement: meter?.last_measurement ?? null, requestKey };
  }, [assetIdForMeters, meterId, meters, isFetching, isError, requestKey]);
}
